import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { DATE_KEY, mondayOf } from '../calendar-layout'

// The day selector above the timeline.
//
// Week mode shows the whole Mon–Sun row, with a dot under the days that hold
// something. Day mode collapses to the selected day alone — and gains chevrons,
// because a single cell would otherwise leave no way to change day at all.

interface ICalendarDayStripProps {
  mode: 'day' | 'week'
  selectedDate: Date
  onSelectDate: (date: Date) => void
  /** ISO dates that hold at least one event, for the dots. */
  datesWithEvents: Set<string>
}

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const ACTIVE_BG = '#2E8FCC'
const DOT_COLOR = '#4DB3FF'

const ChevronButton = ({ label, onClick, children }: { label: string; onClick: () => void; children: JSX.Element }) => (
  <Box
    as="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    border="none"
    bg="transparent"
    color="rgba(255,255,255,0.65)"
    cursor="pointer"
    p="8px"
    display="flex"
    alignItems="center"
    _focus={{ boxShadow: 'none' }}
    _focusVisible={{ boxShadow: 'none' }}
  >
    {children}
  </Box>
)

const CalendarDayStrip = ({ mode, selectedDate, onSelectDate, datesWithEvents }: ICalendarDayStripProps) => {
  const selected = dayjs(selectedDate)

  if (mode === 'day') {
    const iso = selected.format(DATE_KEY)
    return (
      <HStack spacing="8px" justify="center" mb="14px">
        <ChevronButton label="Previous day" onClick={() => onSelectDate(selected.subtract(1, 'day').toDate())}>
          <FiChevronLeft size={18} />
        </ChevronButton>

        <VStack spacing="4px" minW="120px">
          <Text fontSize="10px" color="rgba(255,255,255,0.55)" m={0} letterSpacing="0.05em">
            {selected.format('ddd').toUpperCase()}
          </Text>
          <Text fontSize="15px" fontWeight={700} color="white" m={0}>
            {selected.format('D MMM YYYY')}
          </Text>
          <Box w="4px" h="4px" borderRadius="999px" bg={datesWithEvents.has(iso) ? DOT_COLOR : 'transparent'} />
        </VStack>

        <ChevronButton label="Next day" onClick={() => onSelectDate(selected.add(1, 'day').toDate())}>
          <FiChevronRight size={18} />
        </ChevronButton>
      </HStack>
    )
  }

  const monday = mondayOf(selected)
  const days = Array.from({ length: 7 }, (_, index) => monday.add(index, 'day'))

  return (
    <HStack spacing="6px" mb="14px" align="center">
      <ChevronButton label="Previous week" onClick={() => onSelectDate(monday.subtract(1, 'week').toDate())}>
        <FiChevronLeft size={18} />
      </ChevronButton>

      {days.map((day, index) => {
        const isActive = day.isSame(selected, 'day')
        return (
          <Box
            key={day.format(DATE_KEY)}
            as="button"
            type="button"
            onClick={() => onSelectDate(day.toDate())}
            flex={1}
            minW={0}
            bg="transparent"
            border="none"
            cursor="pointer"
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
          >
            <VStack spacing="4px">
              <VStack spacing="4px" py="8px" w="100%" borderRadius="10px" bg={isActive ? ACTIVE_BG : 'transparent'}>
                <Text
                  fontSize="10px"
                  color={isActive ? 'white' : 'rgba(255,255,255,0.55)'}
                  m={0}
                  letterSpacing="0.05em"
                >
                  {DAY_LABELS[index]}
                </Text>
                <Text
                  fontSize="15px"
                  fontWeight={isActive ? 700 : 500}
                  color={isActive ? 'white' : 'rgba(255,255,255,0.85)'}
                  m={0}
                >
                  {day.format('D')}
                </Text>
              </VStack>
              <Box
                w="4px"
                h="4px"
                borderRadius="999px"
                bg={datesWithEvents.has(day.format(DATE_KEY)) ? DOT_COLOR : 'transparent'}
              />
            </VStack>
          </Box>
        )
      })}

      <ChevronButton label="Next week" onClick={() => onSelectDate(monday.add(1, 'week').toDate())}>
        <FiChevronRight size={18} />
      </ChevronButton>
    </HStack>
  )
}

export default CalendarDayStrip
